import { clsx, type ClassValue } from "clsx";
import { Route } from "next";
import { Session } from "next-auth";
import { ReadonlyURLSearchParams } from "next/navigation";
import { twMerge } from "tailwind-merge";
import { TimezoneAbbreviation } from "./checkout/checkout-types";
import { ENCOMPASS_USER_URL, timezoneMap } from "./constants";
import { PromotionType } from "./shop/shop-types";
var commonPassword = require("common-password");

export function translatePromotionTypeID(promotionTypeID: number): PromotionType {
    switch (promotionTypeID) {
        case 2:
            return "unit discount";
        case 3:
            return "case discount";
        case 4:
            return "unit price";
        case 5:
            return "case price";
        case 6:
            return "percentage";
        default:
            return "unit discount";
    }
}

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

export function verifyCustomerAccess(
    session: Session | null,
    customerID: number[] | number
): boolean {
    if (!session) {
        return false;
    }

    if (session.user.customerIDs === null) {
        return true;
    }

    if (typeof customerID === "number") {
        return session.user.customerIDs.includes(customerID);
    }

    if (customerID instanceof Array) {
        return customerID.every((id) => session.user.customerIDs.includes(id));
    }

    return false;
}

export const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD"
    }).format(amount);
};

export const formatCurrencyAccounting = (amount: number): string => {
    if (!amount) {
        return "0";
    }

    return amount.toLocaleString("en-us", {
        style: "currency",
        currency: "USD",
        currencySign: "accounting",
        minimumFractionDigits: 2
    });
};

export const formatCountString = (
    count: number,
    singular: string,
    plural: string,
    zeroLogic: "zero" | "no" | "No" | "hidden"
) => {
    const absoluteCount = Math.abs(count);
    if (absoluteCount === 0) {
        if (zeroLogic.toLowerCase() === "no") return `${zeroLogic} ${plural}`;
        if (zeroLogic === "zero") return `0 ${plural}`;
        return "";
    } else return `${count} ${absoluteCount > 1 ? plural : singular}`;
};

export const createUrl = (pathname: string, params: URLSearchParams | ReadonlyURLSearchParams) => {
    const paramsString = params.toString();
    const queryString = `${paramsString.length ? "?" : ""}${paramsString}`;

    return `${pathname}${queryString}` as Route;
};

export const getCurrentDateStr = (_date?: Date, addDays?: number): string => {
    const date: Date = _date ? new Date(_date) : new Date();

    if (addDays) {
        date.setDate(date.getDate() + addDays);
    }

    const year: number = date.getFullYear();
    const month: number = date.getMonth() + 1;
    const day: number = date.getDate();

    // Ensure leading zeros if month/day is a single digit
    return `${year}-${month < 10 ? `0${month}` : `${month}`}-${day < 10 ? `0${day}` : `${day}`}`;
};

export function getTimezoneAbbreviation(timezone: string): TimezoneAbbreviation | undefined {
    try {
        const date = new Date();
        const isDST = isDaylightSavingTime(date, timezone);
        const abbreviations = timezoneMap[timezone];

        if (!abbreviations) {
            return undefined;
        }

        // Return DST abbreviation if in DST, otherwise standard time abbreviation
        return isDST ? abbreviations[1] : abbreviations[0];
    } catch (error) {
        console.error(`Error getting timezone abbreviation for ${timezone}:`, error);
        return undefined;
    }
}

function isDaylightSavingTime(date: Date, timezone: string): boolean {
    const januaryOffset = new Date(date.getFullYear(), 0, 1).toLocaleString("en-US", {
        timeZone: timezone,
        timeZoneName: "short"
    });

    const julyOffset = new Date(date.getFullYear(), 6, 1).toLocaleString("en-US", {
        timeZone: timezone,
        timeZoneName: "short"
    });

    return januaryOffset !== julyOffset;
}

export function createCutoffDateTime(timeStr: string, timezone: string, date?: Date): Date {
    const today = date ?? new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");

    const [time, period] = timeStr.split(" ");
    // eslint-disable-next-line prefer-const
    let [hours, minutes] = time!.split(":").map(Number);

    if (period === "PM" && hours !== 12) {
        hours! += 12;
    } else if (period === "AM" && hours === 12) {
        hours = 0;
    }

    const isoString = `${year}-${month}-${day}T${String(hours).padStart(2, "0")}:${String(
        minutes
    ).padStart(2, "0")}:00`;

    const formatter = new Intl.DateTimeFormat("en-US", {
        timeZone: timezone,
        timeZoneName: "longOffset"
    });

    const offset = formatter.format(new Date()).split(" ").pop()?.replace("GMT", "") || "+00:00";

    return new Date(isoString + offset);
}

export const ensureStartsWith = (stringToCheck: string, startsWith: string) =>
    stringToCheck.startsWith(startsWith) ? stringToCheck : `${startsWith}${stringToCheck}`;

export const validateEnvironmentVariables = () => {
    //TODO: populate required environment variables
    const requiredEnvironmentVariables = [""];
    const missingEnvironmentVariables = [] as string[];

    requiredEnvironmentVariables.forEach((envVar) => {
        if (!process.env[envVar]) {
            missingEnvironmentVariables.push(envVar);
        }
    });

    if (missingEnvironmentVariables.length) {
        throw new Error(
            `The following environment variables are missing. The site will not work without them. Variables: ${missingEnvironmentVariables.join(
                "\n"
            )}\n`
        );
    }
};

export function formatDate(date: Date) {
    return new Intl.DateTimeFormat("en-US", {
        dateStyle: "medium",
        timeStyle: "short"
    }).format(new Date(date));
}

export const isObject = (object: unknown): object is Record<string, unknown> => {
    return typeof object === "object" && object !== null && !Array.isArray(object);
};

export const validatePassword = (password1: string, password2: string): [boolean, string] => {
    if (password1 !== password2) {
        return [false, "Passwords must match"];
    }

    if (password1.length < 8 || password1.length > 64) {
        return [false, "Password must be at least 8 characters long"];
    }

    if (commonPassword(password1)) {
        return [false, "Password is too common"];
    }

    return [true, ""];
};

function emojiRegexString(): RegExp {
    return /[#*0-9]\uFE0F?\u20E3|[\xA9\xAE\u203C\u2049\u2122\u2139\u2194-\u2199\u21A9\u21AA\u231A\u231B\u2328\u23CF\u23ED-\u23EF\u23F1\u23F2\u23F8-\u23FA\u24C2\u25AA\u25AB\u25B6\u25C0\u25FB\u25FC\u25FE\u2600-\u2604\u260E\u2611\u2614\u2615\u2618\u2620\u2622\u2623\u2626\u262A\u262E\u262F\u2638-\u263A\u2640\u2642\u2648-\u2653\u265F\u2660\u2663\u2665\u2666\u2668\u267B\u267E\u267F\u2692\u2694-\u2697\u2699\u269B\u269C\u26A0\u26A7\u26AA\u26B0\u26B1\u26BD\u26BE\u26C4\u26C8\u26CF\u26D1\u26D3\u26E9\u26F0-\u26F5\u26F7\u26F8\u26FA\u2702\u2708\u2709\u270F\u2712\u2714\u2716\u271D\u2721\u2733\u2734\u2744\u2747\u2757\u2763\u27A1\u2934\u2935\u2B05-\u2B07\u2B1B\u2B1C\u2B55\u3030\u303D\u3297\u3299]\uFE0F?|[\u261D\u270C\u270D](?:\uFE0F|\uD83C[\uDFFB-\uDFFF])?|[\u270A\u270B](?:\uD83C[\uDFFB-\uDFFF])?|[\u23E9-\u23EC\u23F0\u23F3\u25FD\u2693\u26A1\u26AB\u26C5\u26CE\u26D4\u26EA\u26FD\u2705\u2728\u274C\u274E\u2753-\u2755\u2795-\u2797\u27B0\u27BF\u2B50]|\u26F9(?:\uFE0F|\uD83C[\uDFFB-\uDFFF])?(?:\u200D[\u2640\u2642]\uFE0F?)?|\u2764\uFE0F?(?:\u200D(?:\uD83D\uDD25|\uD83E\uDE79))?|\uD83C(?:[\uDC04\uDD70\uDD71\uDD7E\uDD7F\uDE02\uDE37\uDF21\uDF24-\uDF2C\uDF36\uDF7D\uDF96\uDF97\uDF99-\uDF9B\uDF9E\uDF9F\uDFCD\uDFCE\uDFD4-\uDFDF\uDFF5\uDFF7]\uFE0F?|[\uDF85\uDFC2\uDFC7](?:\uD83C[\uDFFB-\uDFFF])?|[\uDFC3\uDFC4\uDFCA](?:\uD83C[\uDFFB-\uDFFF])?(?:\u200D[\u2640\u2642]\uFE0F?)?|[\uDFCB\uDFCC](?:\uFE0F|\uD83C[\uDFFB-\uDFFF])?(?:\u200D[\u2640\u2642]\uFE0F?)?|[\uDCCF\uDD8E\uDD91-\uDD9A\uDE01\uDE1A\uDE2F\uDE32-\uDE36\uDE38-\uDE3A\uDE50\uDE51\uDF00-\uDF20\uDF2D-\uDF35\uDF37-\uDF7C\uDF7E-\uDF84\uDF86-\uDF93\uDFA0-\uDFC1\uDFC5\uDFC6\uDFC8\uDFC9\uDFCF-\uDFD3\uDFE0-\uDFF0\uDFF8-\uDFFF]|\uDDE6\uD83C[\uDDE8-\uDDEC\uDDEE\uDDF1\uDDF2\uDDF4\uDDF6-\uDDFA\uDDFC\uDDFD\uDDFF]|\uDDE7\uD83C[\uDDE6\uDDE7\uDDE9-\uDDEF\uDDF1-\uDDF4\uDDF6-\uDDF9\uDDFB\uDDFC\uDDFE\uDDFF]|\uDDE8\uD83C[\uDDE6\uDDE8\uDDE9\uDDEB-\uDDEE\uDDF0-\uDDF5\uDDF7\uDDFA-\uDDFF]|\uDDE9\uD83C[\uDDEA\uDDEC\uDDEF\uDDF0\uDDF2\uDDF4\uDDFF]|\uDDEA\uD83C[\uDDE6\uDDE8\uDDEA\uDDEC\uDDED\uDDF7-\uDDFA]|\uDDEB\uD83C[\uDDEE-\uDDF0\uDDF2\uDDF4\uDDF7]|\uDDEC\uD83C[\uDDE6\uDDE7\uDDE9-\uDDEE\uDDF1-\uDDF3\uDDF5-\uDDFA\uDDFC\uDDFE]|\uDDED\uD83C[\uDDF0\uDDF2\uDDF3\uDDF7\uDDF9\uDDFA]|\uDDEE\uD83C[\uDDE8-\uDDEA\uDDF1-\uDDF4\uDDF6-\uDDF9]|\uDDEF\uD83C[\uDDEA\uDDF2\uDDF4\uDDF5]|\uDDF0\uD83C[\uDDEA\uDDEC-\uDDEE\uDDF2\uDDF3\uDDF5\uDDF7\uDDFC\uDDFE\uDDFF]|\uDDF1\uD83C[\uDDE6-\uDDE8\uDDEE\uDDF0\uDDF7-\uDDFB\uDDFE]|\uDDF2\uD83C[\uDDE6\uDDE8-\uDDED\uDDF0-\uDDFF]|\uDDF3\uD83C[\uDDE6\uDDE8\uDDEA-\uDDEC\uDDEE\uDDF1\uDDF4\uDDF5\uDDF7\uDDFA\uDDFF]|\uDDF4\uD83C\uDDF2|\uDDF5\uD83C[\uDDE6\uDDEA-\uDDED\uDDF0-\uDDF3\uDDF7-\uDDF9\uDDFC\uDDFE]|\uDDF6\uD83C\uDDE6|\uDDF7\uD83C[\uDDEA\uDDF4\uDDF8\uDDFA\uDDFC]|\uDDF8\uD83C[\uDDE6-\uDDEA\uDDEC-\uDDF4\uDDF7-\uDDF9\uDDFB\uDDFD-\uDDFF]|\uDDF9\uD83C[\uDDE6\uDDE8\uDDE9\uDDEB-\uDDED\uDDEF-\uDDF4\uDDF7\uDDF9\uDDFB\uDDFC\uDDFF]|\uDDFA\uD83C[\uDDE6\uDDEC\uDDF2\uDDF3\uDDF8\uDDFE\uDDFF]|\uDDFB\uD83C[\uDDE6\uDDE8\uDDEA\uDDEC\uDDEE\uDDF3\uDDFA]|\uDDFC\uD83C[\uDDEB\uDDF8]|\uDDFD\uD83C\uDDF0|\uDDFE\uD83C[\uDDEA\uDDF9]|\uDDFF\uD83C[\uDDE6\uDDF2\uDDFC]|\uDFF3\uFE0F?(?:\u200D(?:\u26A7\uFE0F?|\uD83C\uDF08))?|\uDFF4(?:\u200D\u2620\uFE0F?|\uDB40\uDC67\uDB40\uDC62\uDB40(?:\uDC65\uDB40\uDC6E\uDB40\uDC67|\uDC73\uDB40\uDC63\uDB40\uDC74|\uDC77\uDB40\uDC6C\uDB40\uDC73)\uDB40\uDC7F)?)|\uD83D(?:[\uDC08\uDC26](?:\u200D\u2B1B)?|[\uDC3F\uDCFD\uDD49\uDD4A\uDD6F\uDD70\uDD73\uDD76-\uDD79\uDD87\uDD8A-\uDD8D\uDDA5\uDDA8\uDDB1\uDDB2\uDDBC\uDDC2-\uDDC4\uDDD1-\uDDD3\uDDDC-\uDDDE\uDDE1\uDDE3\uDDE8\uDDEF\uDDF3\uDDFA\uDECB\uDECD-\uDECF\uDEE0-\uDEE5\uDEE9\uDEF0\uDEF3]\uFE0F?|[\uDC42\uDC43\uDC46-\uDC50\uDC66\uDC67\uDC6B-\uDC6D\uDC72\uDC74-\uDC76\uDC78\uDC7C\uDC83\uDC85\uDC8F\uDC91\uDCAA\uDD7A\uDD95\uDD96\uDE4C\uDE4F\uDEC0\uDECC](?:\uD83C[\uDFFB-\uDFFF])?|[\uDC6E\uDC70\uDC71\uDC73\uDC77\uDC81\uDC82\uDC86\uDC87\uDE45-\uDE47\uDE4B\uDE4D\uDE4E\uDEA3\uDEB4-\uDEB6](?:\uD83C[\uDFFB-\uDFFF])?(?:\u200D[\u2640\u2642]\uFE0F?)?|[\uDD74\uDD90](?:\uFE0F|\uD83C[\uDFFB-\uDFFF])?|[\uDC00-\uDC07\uDC09-\uDC14\uDC16-\uDC25\uDC27-\uDC3A\uDC3C-\uDC3E\uDC40\uDC44\uDC45\uDC51-\uDC65\uDC6A\uDC79-\uDC7B\uDC7D-\uDC80\uDC84\uDC88-\uDC8E\uDC90\uDC92-\uDCA9\uDCAB-\uDCFC\uDCFF-\uDD3D\uDD4B-\uDD4E\uDD50-\uDD67\uDDA4\uDDFB-\uDE2D\uDE2F-\uDE34\uDE37-\uDE44\uDE48-\uDE4A\uDE80-\uDEA2\uDEA4-\uDEB3\uDEB7-\uDEBF\uDEC1-\uDEC5\uDED0-\uDED2\uDED5-\uDED7\uDEDC-\uDEDF\uDEEB\uDEEC\uDEF4-\uDEFC\uDFE0-\uDFEB\uDFF0]|\uDC15(?:\u200D\uD83E\uDDBA)?|\uDC3B(?:\u200D\u2744\uFE0F?)?|\uDC41\uFE0F?(?:\u200D\uD83D\uDDE8\uFE0F?)?|\uDC68(?:\u200D(?:[\u2695\u2696\u2708]\uFE0F?|\u2764\uFE0F?\u200D\uD83D(?:\uDC8B\u200D\uD83D)?\uDC68|\uD83C[\uDF3E\uDF73\uDF7C\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D(?:[\uDC68\uDC69]\u200D\uD83D(?:\uDC66(?:\u200D\uD83D\uDC66)?|\uDC67(?:\u200D\uD83D[\uDC66\uDC67])?)|[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uDC66(?:\u200D\uD83D\uDC66)?|\uDC67(?:\u200D\uD83D[\uDC66\uDC67])?)|\uD83E[\uDDAF-\uDDB3\uDDBC\uDDBD])|\uD83C(?:\uDFFB(?:\u200D(?:[\u2695\u2696\u2708]\uFE0F?|\u2764\uFE0F?\u200D\uD83D(?:\uDC8B\u200D\uD83D)?\uDC68\uD83C[\uDFFB-\uDFFF]|\uD83C[\uDF3E\uDF73\uDF7C\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E(?:[\uDDAF-\uDDB3\uDDBC\uDDBD]|\uDD1D\u200D\uD83D\uDC68\uD83C[\uDFFC-\uDFFF])))?|\uDFFC(?:\u200D(?:[\u2695\u2696\u2708]\uFE0F?|\u2764\uFE0F?\u200D\uD83D(?:\uDC8B\u200D\uD83D)?\uDC68\uD83C[\uDFFB-\uDFFF]|\uD83C[\uDF3E\uDF73\uDF7C\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E(?:[\uDDAF-\uDDB3\uDDBC\uDDBD]|\uDD1D\u200D\uD83D\uDC68\uD83C[\uDFFB\uDFFD-\uDFFF])))?|\uDFFD(?:\u200D(?:[\u2695\u2696\u2708]\uFE0F?|\u2764\uFE0F?\u200D\uD83D(?:\uDC8B\u200D\uD83D)?\uDC68\uD83C[\uDFFB-\uDFFF]|\uD83C[\uDF3E\uDF73\uDF7C\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E(?:[\uDDAF-\uDDB3\uDDBC\uDDBD]|\uDD1D\u200D\uD83D\uDC68\uD83C[\uDFFB\uDFFC\uDFFE\uDFFF])))?|\uDFFE(?:\u200D(?:[\u2695\u2696\u2708]\uFE0F?|\u2764\uFE0F?\u200D\uD83D(?:\uDC8B\u200D\uD83D)?\uDC68\uD83C[\uDFFB-\uDFFF]|\uD83C[\uDF3E\uDF73\uDF7C\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E(?:[\uDDAF-\uDDB3\uDDBC\uDDBD]|\uDD1D\u200D\uD83D\uDC68\uD83C[\uDFFB-\uDFFD\uDFFF])))?|\uDFFF(?:\u200D(?:[\u2695\u2696\u2708]\uFE0F?|\u2764\uFE0F?\u200D\uD83D(?:\uDC8B\u200D\uD83D)?\uDC68\uD83C[\uDFFB-\uDFFF]|\uD83C[\uDF3E\uDF73\uDF7C\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E(?:[\uDDAF-\uDDB3\uDDBC\uDDBD]|\uDD1D\u200D\uD83D\uDC68\uD83C[\uDFFB-\uDFFE])))?))?|\uDC69(?:\u200D(?:[\u2695\u2696\u2708]\uFE0F?|\u2764\uFE0F?\u200D\uD83D(?:\uDC8B\u200D\uD83D)?[\uDC68\uDC69]|\uD83C[\uDF3E\uDF73\uDF7C\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D(?:[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uDC66(?:\u200D\uD83D\uDC66)?|\uDC67(?:\u200D\uD83D[\uDC66\uDC67])?|\uDC69\u200D\uD83D(?:\uDC66(?:\u200D\uD83D\uDC66)?|\uDC67(?:\u200D\uD83D[\uDC66\uDC67])?))|\uD83E[\uDDAF-\uDDB3\uDDBC\uDDBD])|\uD83C(?:\uDFFB(?:\u200D(?:[\u2695\u2696\u2708]\uFE0F?|\u2764\uFE0F?\u200D\uD83D(?:[\uDC68\uDC69]|\uDC8B\u200D\uD83D[\uDC68\uDC69])\uD83C[\uDFFB-\uDFFF]|\uD83C[\uDF3E\uDF73\uDF7C\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E(?:[\uDDAF-\uDDB3\uDDBC\uDDBD]|\uDD1D\u200D\uD83D[\uDC68\uDC69]\uD83C[\uDFFC-\uDFFF])))?|\uDFFC(?:\u200D(?:[\u2695\u2696\u2708]\uFE0F?|\u2764\uFE0F?\u200D\uD83D(?:[\uDC68\uDC69]|\uDC8B\u200D\uD83D[\uDC68\uDC69])\uD83C[\uDFFB-\uDFFF]|\uD83C[\uDF3E\uDF73\uDF7C\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E(?:[\uDDAF-\uDDB3\uDDBC\uDDBD]|\uDD1D\u200D\uD83D[\uDC68\uDC69]\uD83C[\uDFFB\uDFFD-\uDFFF])))?|\uDFFD(?:\u200D(?:[\u2695\u2696\u2708]\uFE0F?|\u2764\uFE0F?\u200D\uD83D(?:[\uDC68\uDC69]|\uDC8B\u200D\uD83D[\uDC68\uDC69])\uD83C[\uDFFB-\uDFFF]|\uD83C[\uDF3E\uDF73\uDF7C\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E(?:[\uDDAF-\uDDB3\uDDBC\uDDBD]|\uDD1D\u200D\uD83D[\uDC68\uDC69]\uD83C[\uDFFB\uDFFC\uDFFE\uDFFF])))?|\uDFFE(?:\u200D(?:[\u2695\u2696\u2708]\uFE0F?|\u2764\uFE0F?\u200D\uD83D(?:[\uDC68\uDC69]|\uDC8B\u200D\uD83D[\uDC68\uDC69])\uD83C[\uDFFB-\uDFFF]|\uD83C[\uDF3E\uDF73\uDF7C\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E(?:[\uDDAF-\uDDB3\uDDBC\uDDBD]|\uDD1D\u200D\uD83D[\uDC68\uDC69]\uD83C[\uDFFB-\uDFFD\uDFFF])))?|\uDFFF(?:\u200D(?:[\u2695\u2696\u2708]\uFE0F?|\u2764\uFE0F?\u200D\uD83D(?:[\uDC68\uDC69]|\uDC8B\u200D\uD83D[\uDC68\uDC69])\uD83C[\uDFFB-\uDFFF]|\uD83C[\uDF3E\uDF73\uDF7C\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E(?:[\uDDAF-\uDDB3\uDDBC\uDDBD]|\uDD1D\u200D\uD83D[\uDC68\uDC69]\uD83C[\uDFFB-\uDFFE])))?))?|\uDC6F(?:\u200D[\u2640\u2642]\uFE0F?)?|\uDD75(?:\uFE0F|\uD83C[\uDFFB-\uDFFF])?(?:\u200D[\u2640\u2642]\uFE0F?)?|\uDE2E(?:\u200D\uD83D\uDCA8)?|\uDE35(?:\u200D\uD83D\uDCAB)?|\uDE36(?:\u200D\uD83C\uDF2B\uFE0F?)?)|\uD83E(?:[\uDD0C\uDD0F\uDD18-\uDD1F\uDD30-\uDD34\uDD36\uDD77\uDDB5\uDDB6\uDDBB\uDDD2\uDDD3\uDDD5\uDEC3-\uDEC5\uDEF0\uDEF2-\uDEF8](?:\uD83C[\uDFFB-\uDFFF])?|[\uDD26\uDD35\uDD37-\uDD39\uDD3D\uDD3E\uDDB8\uDDB9\uDDCD-\uDDCF\uDDD4\uDDD6-\uDDDD](?:\uD83C[\uDFFB-\uDFFF])?(?:\u200D[\u2640\u2642]\uFE0F?)?|[\uDDDE\uDDDF](?:\u200D[\u2640\u2642]\uFE0F?)?|[\uDD0D\uDD0E\uDD10-\uDD17\uDD20-\uDD25\uDD27-\uDD2F\uDD3A\uDD3F-\uDD45\uDD47-\uDD76\uDD78-\uDDB4\uDDB7\uDDBA\uDDBC-\uDDCC\uDDD0\uDDE0-\uDDFF\uDE70-\uDE7C\uDE80-\uDE88\uDE90-\uDEBD\uDEBF-\uDEC2\uDECE-\uDEDB\uDEE0-\uDEE8]|\uDD3C(?:\u200D[\u2640\u2642]\uFE0F?|\uD83C[\uDFFB-\uDFFF])?|\uDDD1(?:\u200D(?:[\u2695\u2696\u2708]\uFE0F?|\uD83C[\uDF3E\uDF73\uDF7C\uDF84\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E(?:[\uDDAF-\uDDB3\uDDBC\uDDBD]|\uDD1D\u200D\uD83E\uDDD1))|\uD83C(?:\uDFFB(?:\u200D(?:[\u2695\u2696\u2708]\uFE0F?|\u2764\uFE0F?\u200D(?:\uD83D\uDC8B\u200D)?\uD83E\uDDD1\uD83C[\uDFFC-\uDFFF]|\uD83C[\uDF3E\uDF73\uDF7C\uDF84\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E(?:[\uDDAF-\uDDB3\uDDBC\uDDBD]|\uDD1D\u200D\uD83E\uDDD1\uD83C[\uDFFB-\uDFFF])))?|\uDFFC(?:\u200D(?:[\u2695\u2696\u2708]\uFE0F?|\u2764\uFE0F?\u200D(?:\uD83D\uDC8B\u200D)?\uD83E\uDDD1\uD83C[\uDFFB\uDFFD-\uDFFF]|\uD83C[\uDF3E\uDF73\uDF7C\uDF84\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E(?:[\uDDAF-\uDDB3\uDDBC\uDDBD]|\uDD1D\u200D\uD83E\uDDD1\uD83C[\uDFFB-\uDFFF])))?|\uDFFD(?:\u200D(?:[\u2695\u2696\u2708]\uFE0F?|\u2764\uFE0F?\u200D(?:\uD83D\uDC8B\u200D)?\uD83E\uDDD1\uD83C[\uDFFB\uDFFC\uDFFE\uDFFF]|\uD83C[\uDF3E\uDF73\uDF7C\uDF84\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E(?:[\uDDAF-\uDDB3\uDDBC\uDDBD]|\uDD1D\u200D\uD83E\uDDD1\uD83C[\uDFFB-\uDFFF])))?|\uDFFE(?:\u200D(?:[\u2695\u2696\u2708]\uFE0F?|\u2764\uFE0F?\u200D(?:\uD83D\uDC8B\u200D)?\uD83E\uDDD1\uD83C[\uDFFB-\uDFFD\uDFFF]|\uD83C[\uDF3E\uDF73\uDF7C\uDF84\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E(?:[\uDDAF-\uDDB3\uDDBC\uDDBD]|\uDD1D\u200D\uD83E\uDDD1\uD83C[\uDFFB-\uDFFF])))?|\uDFFF(?:\u200D(?:[\u2695\u2696\u2708]\uFE0F?|\u2764\uFE0F?\u200D(?:\uD83D\uDC8B\u200D)?\uD83E\uDDD1\uD83C[\uDFFB-\uDFFE]|\uD83C[\uDF3E\uDF73\uDF7C\uDF84\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E(?:[\uDDAF-\uDDB3\uDDBC\uDDBD]|\uDD1D\u200D\uD83E\uDDD1\uD83C[\uDFFB-\uDFFF])))?))?|\uDEF1(?:\uD83C(?:\uDFFB(?:\u200D\uD83E\uDEF2\uD83C[\uDFFC-\uDFFF])?|\uDFFC(?:\u200D\uD83E\uDEF2\uD83C[\uDFFB\uDFFD-\uDFFF])?|\uDFFD(?:\u200D\uD83E\uDEF2\uD83C[\uDFFB\uDFFC\uDFFE\uDFFF])?|\uDFFE(?:\u200D\uD83E\uDEF2\uD83C[\uDFFB-\uDFFD\uDFFF])?|\uDFFF(?:\u200D\uD83E\uDEF2\uD83C[\uDFFB-\uDFFE])?))?)/g;
}

const _numberReplacePattern: RegExp = /(C\$)|[$¥€£₱%,]/gi;
const _numberSurroundingParenthesesPattern: RegExp = /^\(\d+\.?\d*\)$/;
const _numberReplaceParenthesesPattern: RegExp = /[\(\)]/g;
const _containsAlphaCharacterPattern: RegExp = /[a-zABD-Z]/g;

export function isNull(value: any) {
    if (
        value === undefined ||
        value === null ||
        value.toString().length === 0 ||
        value.toString().toLowerCase() === "null"
    ) {
        return true;
    } else {
        return false;
    }
}

export function stringToBoolean(str: string): boolean {
    if (!str) return false;

    switch (str.toLowerCase().trim()) {
        case "true":
        case "1":
        case "yes":
        case "y":
            return true;
        case "false":
        case "0":
        case "no":
        case "n":
            return false;
        default:
            return false;
    }
}

export function isNumber(value: any): boolean {
    if (isNull(value) || value.toString().trim() === "" || value !== value) {
        return false;
    }
    if (typeof value === "number") {
        return true;
    }

    if (value.match(_containsAlphaCharacterPattern)) {
        return false;
    }
    return !isNaN(Number(CleanNumberStr(value.toString())));
}

export function CleanNumberStr(value: string): string {
    if (value === null || value === undefined) {
        return "";
    }

    value = value.toString().replace(_numberReplacePattern, "").trim();
    if (value.match(_numberSurroundingParenthesesPattern)) {
        return `-${value.replace(_numberReplaceParenthesesPattern, "")}`;
    }
    try {
        return value.toString();
    } catch (err) {
        return value;
    }
}

export function isInteger(value: any) {
    if (!isNumber(value)) {
        return false;
    }
    if (Number(CleanNumberStr(value.toString())) % 1 === 0) {
        return true;
    }
    return false;
}

export function NullChk(value: any, replacement: any): any {
    if (isNull(value)) {
        return replacement;
    } else {
        return value;
    }
}

export function NullChkStr(value: string | undefined, replacement: string) {
    return NullChk(value, replacement).toString();
}

export function Null2EmptyStr(value: string | undefined) {
    return NullChkStr(value, "");
}

export function Null2NullStr(value: string) {
    return NullChkStr(value, "Null");
}

export function Null2Zero(value: string) {
    return NullChkStr(value, "0");
}

export function Null2ZeroInt(value: string) {
    value = NullChkStr(value, "0");
    if (isInteger(value)) {
        return Number(CleanNumberStr(value));
    }
    return 0;
}

export function JSONEncode(value: string) {
    if (isNull(value)) {
        return "";
    }
    return value
        .toString()
        .replace(/[\\]/g, "\\\\")
        .replace(/[\"]/g, '\\"')
        .replace(/[\/]/g, "\\/")
        .replace(/[\b]/g, "\\b")
        .replace(/[\f]/g, "\\f")
        .replace(/[\n]/g, "\\n")
        .replace(/[\r]/g, "\\r")
        .replace(/[\t]/g, "\\t");
}

export function removeHtml(html: string) {
    if (isNull(html)) return "";
    return html.replace(/(<([^>]+)>)/gi, "");
}

export function convertFromUnixTimestamp(timestamp = 0) {
    const origin_date = new Date();
    const origin = origin_date.setTime(0);
    return origin / 1000 + timestamp;
}

export function convertToUnixTimestamp(date = new Date()) {
    const origin_date = new Date();
    const origin = origin_date.setTime(0);
    const nowUtc = Date.UTC(
        date.getUTCFullYear(),
        date.getUTCMonth(),
        date.getUTCDate(),
        date.getUTCHours(),
        date.getUTCMinutes(),
        date.getUTCSeconds(),
        date.getUTCMilliseconds()
    );
    const diff = nowUtc - origin;
    return Math.floor(diff / 1000);
}

export function isEmail(email: string) {
    const regex =
        /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return regex.test(String(email).toLowerCase());
}

export function Left(value: string, n: number) {
    if (n <= 0) {
        return "";
    } else if (isNull(value)) {
        return "";
    } else if (n > value.toString().length) {
        return value.toString();
    } else {
        return value.toString().substring(0, n);
    }
}

export function Right(value: string, n: number) {
    if (n <= 0) {
        return "";
    } else if (isNull(value)) {
        return "";
    } else if (n > value.toString().length) {
        return value.toString();
    } else {
        const stringLength = value.toString().length;
        return value.toString().substring(stringLength, stringLength - n);
    }
}

export function Round(value: string | number, decimalPlaces: number) {
    if (!isNumber(value)) {
        return undefined;
    }
    value = CleanNumberStr(value.toString());
    return Number(Number(value).toFixed(NullChk(decimalPlaces, 0)));
}

export function NowAsMountainTime() {
    const mountainTime = new Date().toLocaleString("en-US", {
        timeZone: "America/Denver"
    });
    return mountainTime.replace(",", "");
}

export function NowAsPacificTime() {
    const mountainTime = new Date().toLocaleString("en-US", {
        timeZone: "America/Los_Angeles"
    });
    return mountainTime.replace(",", "");
}

export function TodayStr() {
    const date = (NowAsPacificTime() || "").split(" ")[0];
    return date;
}

export function StringToEnum(value: string, dict: { [id: string]: unknown }) {
    for (const key in dict) if (dict[key] === value) return key;

    return undefined; // Couldn't find it
}

export function base64UrlDecode(encoded: string) {
    const byteArr = Buffer.from(encoded, "base64");
    const urlEncoded = byteArr.toString("utf-8");
    return decodeURIComponent(urlEncoded);
}

export function base64UrlEncode(plainText: string) {
    const urlEncoded = encodeURIComponent(plainText);
    const byteArr = Buffer.from(urlEncoded, "utf-8");
    return byteArr.toString("base64");
}

export function stripEmoji(text: string) {
    return Null2EmptyStr(text).replace(emojiRegexString(), "");
}

export function SQLSafe(text: string) {
    return Null2EmptyStr(text).replace(/"/g, "'");
}

export function fileNameSafe(text: string) {
    let value = removeHtml(text);
    value = stripEmoji(value);
    value = value.replace(/[\/\?<>\\:\*\|"]/g, ""); // illegal chars for operating system's file systems https://kb.acronis.com/content/39790
    // eslint-disable-next-line no-control-regex
    value = value.replace(/[\x00-\x1f\x80-\x9f]/g, ""); // remove unicode control codes http://en.wikipedia.org/wiki/C0_and_C1_control_codes
    value = value.replace(/^\.+$/, ""); // removed unix reserved file system chars
    value = value.replace(/^(con|prn|aux|nul|com[0-9]|lpt[0-9])(\..*)?$/i, ""); // remove windows reserved file system chars
    value = value.replace(/[\. ]+$/, ""); // remove windows trailing reserved file system chars
    value = value.replace(new RegExp(" ", ""), "");
    return Left(value, 255); // http://unix.stackexchange.com/questions/32795/what-is-the-maximum-allowed-filename-and-folder-size-with-ecryptfs
}

export function decodeHTMLEntities(encodedString: string | null | undefined): string {
    if (isNull(encodedString) || typeof encodedString !== "string") {
        return "";
    }

    return (
        encodedString
            // Handle decimal HTML entities (e.g., &#34;)
            .replace(/&#(\d+);/g, (match: string, dec: string): string =>
                String.fromCharCode(parseInt(dec, 10))
            )
            // Handle hexadecimal HTML entities (e.g., &#x22;)
            .replace(/&#x([0-9A-F]+);/gi, (match: string, hex: string): string =>
                String.fromCharCode(parseInt(hex, 16))
            )
            // Handle common named entities
            .replace(/&quot;/g, '"')
            .replace(/&apos;/g, "'")
            .replace(/&amp;/g, "&")
            .replace(/&lt;/g, "<")
            .replace(/&gt;/g, ">")
    );
}

export function createSlackLinkToEncompassUser(userID: string, displayName?: string) {
    return `<${ENCOMPASS_USER_URL.replace("{{USER_ID}}", userID)}|${displayName || userID}>`;
}

export function isValidDate(value: unknown): boolean {
    if (value === null || value === undefined || value === "") {
        return false;
    }

    if (value instanceof Date) {
        return !isNaN(value.getTime());
    }

    if (typeof value === "number") {
        return !isNaN(new Date(value).getTime());
    }

    if (typeof value === "string") {
        const date = new Date(value);

        return isValidDate(date);
    }

    return false;
}

// for testing if you need to delay something loading
export function delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

export function toTitleCaseWithAcronyms(str: string): string {
    if (isNull(str)) {
        return "";
    }
    // Common cannabinoid and cannabis-related acronyms
    const acronyms = [
        "THC",
        "CBD",
        "CBN",
        "CBG",
        "CBC",
        "THCA",
        "CBDA",
        "CBGA",
        "CBCA",
        "THCV",
        "CBDV",
        "CBGV",
        "THCP",
        "CBDP",
        "HHC",
        "D8",
        "D9",
        "D10",
        "DELTA8",
        "DELTA9",
        "DELTA10",
        "CO2",
        "OG",
        "SFV"
    ];

    return str
        .toLowerCase()
        .split(" ")
        .map((word) => {
            // Remove punctuation for acronym checking but preserve it in output
            const cleanWord = word.replace(/[^\w]/g, "");

            // Check if the clean word (without punctuation) is an acronym
            if (acronyms.includes(cleanWord.toUpperCase())) {
                // Replace the letters in the original word with uppercase, preserving punctuation
                return word.replace(/[a-zA-Z]/g, (char, index) => {
                    const cleanIndex =
                        word.substring(0, index + 1).replace(/[^\w]/g, "").length - 1;
                    const upperAcronym = cleanWord.toUpperCase();
                    return upperAcronym[cleanIndex] || char;
                });
            }

            // Standard title case: capitalize first letter
            return word.charAt(0).toUpperCase() + word.slice(1);
        })
        .join(" ");
}
